import type { Sprint, TicketMeta } from '@shared/schemas';
import { SPRINTS, sprintById } from '@shared/data/sprints';
import { ticketById, ticketsBySprint } from '@shared/data/tickets';
import { GLOSSARY } from '@shared/data/glossary';
import type { GlossaryTerm } from '@shared/data/glossary';

/**
 * Static-content endpoints · bundle-direct (parity-ledger CF-SM-STATIC-CONTENT-DROP).
 *
 * The 6 `/api/sprints` · `/api/tickets` · `/api/glossary` Hono routes were dropped
 * during SM.4 migration (Genie 2026-05-23) — course content already ships in the
 * client bundle (~4.6 MB), no point round-tripping to a server. The wrapper
 * surface is preserved so existing callers (e.g., Board.tsx → getSprintTickets)
 * keep working unchanged · the read just resolves from `@shared/data/*` instead
 * of crossing the network.
 *
 * Returns `Promise` to keep the surface async-compatible with the previous
 * HTTP-backed shape (callers `await api.staticContent.*`).
 */

class NotFoundError extends Error {
  readonly code = 'notFound';
  constructor(message: string) {
    super(message);
    this.name = 'NotFoundError';
  }
}

export const staticContentEndpoints = {
  async getSprints(): Promise<Sprint[]> {
    return [...SPRINTS];
  },

  async getSprint(id: string): Promise<Sprint> {
    const sprint = sprintById(id);
    if (!sprint) throw new NotFoundError(`Sprint ${id} not found`);
    return sprint;
  },

  async getSprintTickets(sprintId: string): Promise<TicketMeta[]> {
    return ticketsBySprint(sprintId);
  },

  async getTicket(id: string): Promise<TicketMeta> {
    const ticket = ticketById(id);
    if (!ticket) throw new NotFoundError(`Ticket ${id} not found`);
    return ticket;
  },

  async getGlossary(): Promise<GlossaryTerm[]> {
    return [...GLOSSARY];
  },
};
